'use client';

// The alternation and the render. The chart that goes with them lives in
// UpsellChart.tsx — kept apart because the layouts place them differently and
// only one of the two ever needs to change.

import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ShowcaseRoom } from './rooms';
import UpsellRoom, { DEFAULT_ORBIT, type Orbit } from './UpsellRoom';

export type ABState = 'before' | 'after';

const PERIOD_MS = 3400;

/** Drag sensitivity. A full frame width turns the room roughly half a turn,
 *  which is enough to look behind a speaker and slow enough to stop on. */
const ORBIT_RAD_PER_PX = 0.005;

/** How far a touch has to travel before its direction can be read. Under the
 *  browser's own scroll slop (8px on Android), deliberately: past that point
 *  the page has already taken the gesture and there is nothing left to claim. */
const INTENT_PX = 6;

/** The wipe's two state labels. One recipe, so they cannot drift apart in
 *  anything but colour — which is the one thing that should differ. */
const WIPE_LABEL =
  'pointer-events-none absolute top-3 rounded-full bg-[color-mix(in_srgb,var(--ed-bg)_78%,transparent)] '
  + 'px-3 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] backdrop-blur-[2px]';

export interface Alternate {
  state: ABState;
  frozen: boolean;
  /** Pick a state explicitly. Stops the alternation, per the brief — picking
   *  the state already showing hands it back. */
  pick: (s: ABState) => void;
}

/** `periodMs`: how long each state holds. A host that alternates SOUND as
 *  well as the picture wants longer than the picture alone — an ear needs
 *  a few seconds of a room to hear its decay. */
export function useAlternate(periodMs = PERIOD_MS): Alternate {
  const [state, setState] = useState<ABState>('before');
  const [frozen, setFrozen] = useState(false);

  // Reduced motion is checked here rather than pushed into `frozen` by a
  // second effect: setting state from an effect body just to express "do not
  // start the timer" is a cascading render for nothing, and the timer is the
  // only thing the preference actually governs.
  useEffect(() => {
    if (frozen || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setInterval(() => setState((s) => (s === 'before' ? 'after' : 'before')), periodMs);
    return () => clearInterval(t);
  }, [frozen, periodMs]);

  return {
    state,
    frozen,
    pick: (s) => {
      if (frozen && s === state) { setFrozen(false); return; }
      setState(s);
      setFrozen(true);
    },
  };
}

/** One room, live. Before/after is `panels`, so the camera never moves and
 *  nothing cross-fades — the treatment appears in place, which is what the
 *  editor's own compare toggle does. */
export function RoomShot({
  room, state, className = '',
}: { room: ShowcaseRoom; state: ABState; className?: string }) {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <UpsellRoom room={room} panels={state === 'after'} />
    </div>
  );
}

/** One frame, split by a draggable divider: untreated left of the handle,
 *  treated right of it. Nothing else on this screen states "these are two
 *  states of the same room" as flatly as a seam you can push across it —
 *  tabs and an alternation both ask the viewer to remember what they just
 *  saw, and a wipe does not.
 *
 *  The handle sweeps on its own until first touched, for the same reason the
 *  alternation does: a still image of a wipe just looks like a photo with a
 *  line on it. `pointer` events, not mouse — this has to work on a trackpad
 *  and under a thumb identically. */
export function RoomWipe({
  room, fill, reveal, className = '',
}: {
  room: ShowcaseRoom;
  fill?: number;
  /** Show this state whole. The divider stops being a control and becomes
   *  the transition: it glides to the edge that uncovers `reveal`, so a
   *  switch between the two sweeps one room into the other. The homepage's
   *  listening room drives it from the same Before/After the audio plays;
   *  left unset, the frame is the draggable comparison described above. */
  reveal?: ABState;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pct, setPct] = useState(50);
  const [held, setHeld] = useState(false);
  /** Where the divider is drawn: the sweep or the hand, unless the host has
   *  asked for one state whole (0 uncovers the treated room, 100 the bare). */
  const shown = reveal === 'after' ? 0 : reveal === 'before' ? 100 : pct;
  const [orbit, setOrbit] = useState<Orbit>(DEFAULT_ORBIT);
  const [nearHandle, setNearHandle] = useState(false);
  // Which gesture is in flight, where it started, and whether it is ours to
  // finish (see the touchmove effect below — a touch is only provisionally
  // ours until its direction has been read). A ref, not state: it is read
  // inside pointermove on every frame and re-rendering on it would do nothing
  // but cost.
  const drag = useRef<
    { mode: 'wipe' | 'orbit'; x: number; y: number; orbit: Orbit; claimed: boolean } | null
  >(null);

  useEffect(() => {
    if (held || reveal || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const started = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      // A wide, slow sweep: nearly the full frame either way, so both states
      // are seen whole rather than as two thirds of themselves. A shallow one
      // read as a line wobbling near the middle — the two halves never changed
      // enough for the eye to register them as two rooms.
      setPct(50 + Math.sin((t - started) / 2600) * 36);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [held, reveal]);

  const box = () => ref.current?.getBoundingClientRect() ?? null;

  const wipeTo = (clientX: number) => {
    const b = box();
    if (!b || b.width === 0) return;
    setPct(Math.min(98, Math.max(2, ((clientX - b.left) / b.width) * 100)));
  };

  /** Within grabbing distance of the divider, in px. Generous — the line is
   *  3px wide and nobody aims at a 3px target; the grip is roughly this size
   *  and is what advertises the zone. */
  const GRAB = 26;
  const onHandle = (clientX: number) => {
    // Revealing, the divider is not a control: every drag turns the room.
    if (reveal) return false;
    const b = box();
    if (!b || b.width === 0) return false;
    return Math.abs(clientX - (b.left + (b.width * pct) / 100)) <= GRAB;
  };

  // Whose gesture is a touch drag — ours, or the page's?
  //
  // A phone hands vertical panning to the browser by default, and the browser
  // takes it MID-DRAG: the pointer stream stops dead (pointercancel) the moment
  // a drag drifts a few pixels off the horizontal, which every real thumb does.
  // That is why this frame used to be unusable on a phone while working
  // perfectly with a mouse.
  //
  // `touch-action: none` would end it by taking the frame out of the page's
  // scroll entirely. It is the wrong one here: this room is half a phone screen
  // tall on a marketing page people arrive at to scroll. The guided 2D plan
  // canvases split by what is under the finger instead (useTouchGrab), because
  // their draggable targets are small; this one is gesture all the way across.
  //
  // So the frame keeps `touch-action: pan-y pinch-zoom` and splits the gesture
  // by direction instead: horizontal is ours, vertical is the page's, and a
  // drag that started on the handle is always ours — the handle only reads
  // clientX, so the vertical travel in a thumb's "horizontal" drag is noise
  // rather than intent. Once claimed a gesture stays claimed, which is what
  // lets an orbit tilt after it has turned.
  //
  // Registered by hand rather than as an `onTouchMove` prop: React attaches
  // touchmove passively at the root, where preventDefault() does nothing.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onTouchMove = (e: TouchEvent) => {
      const d = drag.current;
      if (!d) return;
      if (!d.claimed) {
        const t = e.touches[0];
        if (!t) return;
        const dx = Math.abs(t.clientX - d.x);
        const dy = Math.abs(t.clientY - d.y);
        if (Math.max(dx, dy) < INTENT_PX) return;
        if (dy > dx) { drag.current = null; return; }
        d.claimed = true;
      }
      // Keep preventing for the rest of the gesture: a scroll can still start
      // on any later move we let through.
      if (e.cancelable) e.preventDefault();
    };
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, []);

  return (
    <div
      ref={ref}
      data-testid="room-wipe"
      className={`relative select-none overflow-hidden ${
        nearHandle ? 'cursor-ew-resize' : 'cursor-grab'
      } ${className}`}
      // Vertical stays the page's to scroll; the effect above takes the
      // gestures that turn out to be ours. `pinch-zoom` is spelt out because
      // `pan-y` alone would also cost this frame the browser's own zoom.
      style={{ touchAction: 'pan-y pinch-zoom' }}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        // A mouse or a pen is unambiguous; only a touch has to earn the
        // gesture off the page first, and a grab on the handle earns it at
        // once — nothing else lives there to mistake it for.
        const wipe = onHandle(e.clientX);
        const claimed = wipe || e.pointerType !== 'touch';
        if (wipe) {
          // Only a wipe freezes the sweep. Orbiting is a different gesture
          // about a different thing, and stopping the comparison because
          // someone turned the room around would be a non-sequitur.
          setHeld(true);
          drag.current = { mode: 'wipe', x: e.clientX, y: e.clientY, orbit, claimed };
          wipeTo(e.clientX);
        } else {
          drag.current = { mode: 'orbit', x: e.clientX, y: e.clientY, orbit, claimed };
        }
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) { setNearHandle(onHandle(e.clientX)); return; }
        // Still the page's for all we know. Nothing is lost by waiting: every
        // delta below is measured from where the gesture started, so the first
        // move after it is claimed carries the whole travel.
        if (!d.claimed) return;
        if (d.mode === 'wipe') { wipeTo(e.clientX); return; }
        setOrbit({
          az: d.orbit.az - (e.clientX - d.x) * ORBIT_RAD_PER_PX,
          // Clamped short of the poles: at either one the room flattens into
          // a plan or a ceiling and the wipe stops meaning anything.
          el: Math.min(1.32, Math.max(0.06, d.orbit.el + (e.clientY - d.y) * ORBIT_RAD_PER_PX)),
        });
      }}
      onPointerUp={() => { drag.current = null; }}
      onPointerCancel={() => { drag.current = null; }}
      onPointerLeave={() => setNearHandle(false)}
    >
      {/* Two live renders of the same room on ONE camera — `orbit` is the same
          object in both, so turning the room turns both halves together and
          the seam stays a seam. The untreated one is clipped to the handle
          rather than faded, so the halves meet on a hard edge and the eye
          compares the same wall. */}
      <UpsellRoom room={room} panels orbit={orbit} fill={fill} />
      <div
        className={`absolute inset-0${reveal ? ' room-wipe-glide' : ''}`}
        style={{ clipPath: `inset(0 ${100 - shown}% 0 0)` }}
      >
        <UpsellRoom room={room} panels={false} orbit={orbit} fill={fill} />
      </div>

      {/* The divider and its grip. Thick enough to read as an edge you can
          push rather than a scratch on the picture, and the grip is what says
          so before anyone tries. Both are pointer-transparent: the frame owns
          every gesture and decides which one it is from where it started. */}
      {reveal ? (
        // Keyed on the state, so each switch replays the flash: the line is
        // seen as it sweeps across and gone once it rests on the frame's edge.
        <div
          key={reveal}
          aria-hidden
          className="room-wipe-glide room-wipe-flash pointer-events-none absolute inset-y-0 -ml-[1.5px] w-[3px] bg-[var(--ed-text)]"
          style={{ left: `${shown}%` }}
        />
      ) : (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 -ml-[1.5px] w-[3px] bg-[var(--ed-text)] opacity-80"
          style={{ left: `${pct}%` }}
        />
      )}
      {!reveal && (
        <div
          aria-hidden
          className={`pointer-events-none absolute top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-px rounded-full border bg-[var(--ed-bg)] ed-elev-2 transition-colors ${
            nearHandle
              ? 'border-[var(--ed-text-dim)] text-[var(--ed-text)]'
              : 'border-[var(--ed-border-bright)] text-[var(--ed-text-dim)]'
          }`}
          style={{ left: `${pct}%` }}
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2.25} />
          <ChevronRight className="h-4 w-4" strokeWidth={2.25} />
        </div>
      )}

      {/* Both states, named, at the top where a room render has the least
          going on. Each sits on its own plate so it survives whatever is
          behind it, and "after" carries full ink against "before"'s dim —
          the pair states the direction of the change, not just its ends. */}
      {/* Revealing, the host names the state it asked for, so the frame's own
          pair would label a half that is not there. */}
      {!reveal && (
        <>
          <span className={`${WIPE_LABEL} left-3 text-[var(--ed-text-dim)]`}>Before</span>
          <span className={`${WIPE_LABEL} right-3 text-[var(--ed-text)]`}>After</span>
        </>
      )}
    </div>
  );
}
